import React from 'react';
import {
  AspectRatio, Badge, Box, Button, Heading, HStack, Stack, Text,
} from '@chakra-ui/react';
import Image from 'next/image';
import router from 'next/router';
import formatMoney from '../../utils/money';
import EventFacts from './EventFacts';
import { availabilityNote, isSoldOut, priceSummary } from './eventSummary';

interface Props { event: any }

/**
 * One event in a multi-event listing.
 *
 * Deliberately not `ItemCard`: that component leads with a title and treats
 * everything else as a subtitle, which is right for a course but wrong for a
 * one-off event where the date is the fact people scan for. It also stacked a
 * status badge over the title, obscuring it. Courses keep `ItemCard` unchanged.
 */
const EventListCard = ({ event }: Props) => {
  const tiers = event.tiers || [];
  const soldOut = isSoldOut(tiers);
  const note = availabilityNote(tiers);
  const price = priceSummary(tiers);
  const start = event.startDateTime ? new Date(event.startDateTime) : null;

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      height="100%"
      display="flex"
      flexDirection="column"
      transition="all 0.3s ease"
      opacity={soldOut ? 0.75 : 1}
      _hover={soldOut ? {} : {
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box position="relative">
        <AspectRatio ratio={16 / 9}>
          {event.image ? (
            <Image
              src={event.image.split('?')[0]}
              alt={event.name}
              fill
              unoptimized
              style={{ objectFit: 'cover', filter: soldOut ? 'grayscale(1)' : undefined }}
            />
          ) : (
            <Box bgGradient="linear(135deg, blue.900, violet.900)" />
          )}
        </AspectRatio>

        {/* Date block: the single most important fact about a one-off event. */}
        {start && (
          <Box
            position="absolute"
            top={3}
            left={3}
            bg="white"
            borderRadius="xl"
            px={3}
            py={2}
            textAlign="center"
            minW="54px"
            boxShadow="0 4px 12px rgba(0,0,0,0.18)"
          >
            <Text fontSize="11px" textTransform="uppercase" letterSpacing="wider" color="gray.500">
              {start.toLocaleString('en-GB', { month: 'short' })}
            </Text>
            <Text fontSize="26px" lineHeight="1" fontWeight="700">{start.getDate()}</Text>
            <Text fontSize="10px" color="gray.500">{start.getFullYear()}</Text>
          </Box>
        )}

        {note && (
          <Badge
            position="absolute"
            top={3}
            right={3}
            colorScheme={soldOut ? 'gray' : 'orange'}
            variant="solid"
          >
            {note}
          </Badge>
        )}
      </Box>

      <Stack p={5} spacing={4} flexGrow={1}>
        <Stack spacing={2}>
          <Heading as="h3" size="md" lineHeight="1.25">{event.name}</Heading>
          <EventFacts
            name={event.name}
            startDateTime={event.startDateTime}
            endDateTime={event.endDateTime}
            address={event.address}
          />
        </Stack>

        {price.kind === 'bands' && (
          <HStack spacing={2} wrap="wrap">
            {price.bands.map((band) => (
              <Text
                key={`${band.name}-${band.price}`}
                bg="blue.50"
                borderRadius="md"
                px={2.5}
                py={1}
                fontSize="13px"
              >
                {`${band.name} ${formatMoney(band.price)}`}
              </Text>
            ))}
          </HStack>
        )}

        {price.kind === 'from' && (
          <Text fontSize="15px">
            From
            {' '}
            <Text as="span" fontWeight="600">{formatMoney(price.from as number)}</Text>
          </Text>
        )}

        <Box mt="auto" pt={1}>
          <Button
            w="100%"
            variant="solid"
            colorScheme="yellow"
            isDisabled={soldOut}
            onClick={() => router.push(`/event/${event.id}`)}
          >
            {soldOut ? 'Sold out' : 'Choose tickets'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default EventListCard;
